import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "content" }
const _hoisted_2 = { class: "flex flex-row items-end" }
const _hoisted_3 = { class: "flex flex-col" }
const _hoisted_4 = { class: "sub-steps gordita-400-12 text-Text/text_low pb-1.5" }
const _hoisted_5 = /*#__PURE__*/_createElementVNode("i", { class: "uil-copy mr-1.5" }, null, -1)
const _hoisted_6 = { class: "agents flex flex-col gap-6 whitespace-pre-line text-Text/text_medium gordita-400-13 leading-[150%]" }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("i", { class: "uil-copy mr-1.5" }, null, -1)
const _hoisted_8 = {
  key: 0,
  class: "mt-[8px] ml-[8px]"
}
const _hoisted_9 = ["href"]
const _hoisted_10 = { class: "sub-steps gordita-400-12 text-Text/text_low pb-1.5" }
const _hoisted_11 = { class: "agents whitespace-pre-line text-Text/text_medium gordita-400-13 leading-[150%]" }
const _hoisted_12 = ["href"]

import { computed, inject, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { IncrediButton } from 'incredi-ui';
import IbxScopedCss from '@/components/IbxScopedCss/IbxScopedCss.vue';
import { useCopyToClipboard } from '@/services/notify/copy-to-clipboard';
import { envConfig } from '@/services/features';
import { objectMapper } from '@/utils';
import { MatomoCategories, MatomoExternalLinkActions } from '@/services/matomo/matomo.const';
import { useMatomo } from '@/services/matomo/matomo.service';
import { useCommonDataQuery } from '@/graphql/codegen/generated/graphql';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import { Accordion, StepProgress, BuildsContent } from './components';
// eslint-disable-next-line import/extensions, import/no-unresolved
import type { Step } from './steps';
import { useGettingStarted } from './getting-started';


export default /*#__PURE__*/_defineComponent({
  __name: 'getting-started',
  setup(__props) {

const { t } = useI18n();

const selected = ref('');
const isTroubleshootingButtonClicked = ref(false);

const copyCommandToClipboard = useCopyToClipboard();
const matomo = useMatomo();

const { sdrMail, invitationVideo, invitationInstall } = envConfig;
const host = '{{host}}';
const port = '{{port}}';
const { agentsAdded, buildsCreated, hostAddress, ibManagerPort, daysLeft } = useGettingStarted();
const coordinatorService = inject(IKCoordinatorsService);
const GQL_commonData = useCommonDataQuery(() => ({ coordinatorId: coordinatorService?.selectedCoordinator.value || '' }));

const invitation = computed(() => invitationInstall.replace(host, hostAddress.value).replace(port, `${ibManagerPort.value}`));
const isFreeDevLicense = computed(() => GQL_commonData?.result?.value?.ibManagerEntities?.license?.windows?.isFreeDev);

const handleCopyInviteLink = () => {
  copyCommandToClipboard(invitation.value);
  matomo?.trackEvent({
    category: MatomoCategories.GettingStarted,
    action: MatomoExternalLinkActions.Click,
    name: 'agentInviteButton',
  });
};

const steps = computed<Step[]>(() => [
  {
    name: 'agents',
    title: t('gettingStarted.steps.agents.title'),
    isCompleted: isFreeDevLicense.value ? (agentsAdded.value || 0) >= 1 : (agentsAdded.value || 0) >= 3,
    subSteps: {
      completed: agentsAdded.value || 0,
      toComplete: isFreeDevLicense.value ? 1 : 3,
    },
  },
  {
    name: 'builds',
    title: t('gettingStarted.steps.builds.title'),
    isCompleted: (buildsCreated.value || 0) >= 5,
    subSteps: {
      completed: buildsCreated.value || 0,
      toComplete: 5,
    },
  },
  {
    name: 'license',
    title: t('gettingStarted.steps.license.title'),
    isCompleted: false,
    subSteps: {
      completed: 0,
      toComplete: 1,
    },
  },
]);

const agentsOrdinal = objectMapper(
  {
    0: 'zero',
    1: 'one',
    2: 'two',
  },
  'other',
);

const buildsOrdinal = objectMapper(
  {
    0: 'zero',
    1: 'one',
    2: 'two',
    3: 'three',
    4: 'four',
  },
  'other',
);

const onSelect = (n: string) => {
  selected.value = n;
};

const contactUs = (e: MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
  copyCommandToClipboard(sdrMail);
  return false;
};

watch(steps, () => {
  const firstUncompleted = steps.value.find(step => !step.isCompleted);
  if (firstUncompleted) {
    selected.value = firstUncompleted.name;
  }
});

return (_ctx: any,_cache: any) => {
  const _component_q_icon = _resolveComponent("q-icon")!

  return (_openBlock(), _createBlock(IbxScopedCss, { style: {"margin":"0 auto","width":"840px","display":"inline-flex","flex-direction":"column","overflow-y":"scroll"} }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(Accordion), {
          steps: _unref(steps),
          selected: selected.value,
          onOnSelect: onSelect
        }, {
          "step-header-agents": _withCtx(({ completed, toComplete, isSelected }) => [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(t)(
                    _unref(isFreeDevLicense) && completed === 0
                      ? `gettingStarted.steps.agents.subSteps_${_unref(agentsOrdinal)[completed]}_free_dev`
                      : `gettingStarted.steps.agents.subSteps_${_unref(agentsOrdinal)[completed]}`,
                    { count: completed, total: toComplete },
                  )), 1),
                _createVNode(_unref(StepProgress), {
                  "data-testid": "gettingStarted-steps-builds-progress",
                  style: {"width":"236px"},
                  completed: completed,
                  "to-complete": toComplete
                }, null, 8, ["completed", "to-complete"])
              ]),
              (!isSelected)
                ? (_openBlock(), _createBlock(_unref(IncrediButton), {
                    key: 0,
                    class: "ml-3",
                    size: "tiny",
                    "data-testid": "gettingStarted-steps-agents-link",
                    disabled: !_unref(invitation),
                    onClick: handleCopyInviteLink
                  }, {
                    default: _withCtx(() => [
                      _hoisted_5,
                      _createTextVNode(_toDisplayString(_unref(t)('gettingStarted.steps.agents.link')), 1)
                    ]),
                    _: 1
                  }, 8, ["disabled"]))
                : _createCommentVNode("", true)
            ])
          ]),
          "content-agents": _withCtx(() => [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", null, _toDisplayString(_unref(t)('gettingStarted.steps.agents.content_1')), 1),
              _createElementVNode("div", null, [
                _createElementVNode("span", null, _toDisplayString(_unref(t)('gettingStarted.steps.agents.content_2')), 1),
                _createVNode(_unref(IncrediButton), {
                  class: "mt-[4px]",
                  size: "tiny",
                  "data-testid": "gettingStarted-steps-agents-link",
                  onClick: handleCopyInviteLink
                }, {
                  default: _withCtx(() => [
                    _hoisted_7,
                    _createTextVNode(_toDisplayString(_unref(t)('gettingStarted.steps.agents.link')), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_unref(IncrediButton), {
                  design: "ghost-green",
                  class: "mt-[4px]",
                  size: "tiny",
                  "data-testid": "gettingStarted-steps-agents-troubleshooting",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (isTroubleshootingButtonClicked.value = !isTroubleshootingButtonClicked.value))
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", null, _toDisplayString(_unref(t)('gettingStarted.steps.agents.troubleshootingButton')), 1),
                    _createVNode(_component_q_icon, {
                      size: "24px",
                      style: {"padding-left":"4px"},
                      name: isTroubleshootingButtonClicked.value ? 'icon-uil uil-angle-up' : 'icon-uil uil-angle-down'
                    }, null, 8, ["name"])
                  ]),
                  _: 1
                }),
                (isTroubleshootingButtonClicked.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(_unref(t)('gettingStarted.steps.agents.troubleshootingContent')), 1))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", null, [
                _createElementVNode("span", null, _toDisplayString(_unref(t)('gettingStarted.steps.agents.content_2a')), 1),
                _createElementVNode("a", {
                  href: _unref(invitationVideo),
                  class: "text-Text/text_primary gordita-500-13-m hover:underline",
                  target: "_blank"
                }, _toDisplayString(_unref(t)('gettingStarted.steps.agents.content_2_video')), 9, _hoisted_9),
                _createElementVNode("span", null, _toDisplayString(_unref(t)('gettingStarted.steps.agents.content_2b')), 1)
              ])
            ])
          ]),
          "step-header-builds": _withCtx(({ completed, toComplete }) => [
            _createElementVNode("div", _hoisted_10, _toDisplayString(_unref(t)(`gettingStarted.steps.builds.subSteps_${_unref(buildsOrdinal)[completed]}`, { count: completed, total: toComplete })), 1),
            _createVNode(_unref(StepProgress), {
              "data-testid": "gettingStarted-steps-builds-progress",
              style: {"width":"236px"},
              completed: completed,
              "to-complete": toComplete
            }, null, 8, ["completed", "to-complete"])
          ]),
          "content-builds": _withCtx(() => [
            _createVNode(_unref(BuildsContent), {
              "is-free-dev": _unref(isFreeDevLicense) || false
            }, null, 8, ["is-free-dev"])
          ]),
          "step-header-license": _withCtx(({ completed, toComplete }) => [
            _createVNode(_unref(StepProgress), {
              "data-testid": "gettingStarted-steps-license-progress",
              style: {"width":"236px"},
              completed: completed,
              "to-complete": toComplete
            }, null, 8, ["completed", "to-complete"])
          ]),
          "content-license": _withCtx(() => [
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("span", null, _toDisplayString(_unref(t)('gettingStarted.steps.license.content', { days: _unref(daysLeft) })), 1),
              _createElementVNode("a", {
                href: `mailto://${_unref(sdrMail)}`,
                class: "text-Text/text_primary gordita-500-13 hover:underline",
                onClick: contactUs
              }, _toDisplayString(_unref(sdrMail)), 9, _hoisted_12)
            ])
          ]),
          _: 1
        }, 8, ["steps", "selected"])
      ])
    ]),
    _: 1
  }))
}
}

})